import { useState, useEffect } from "react";
import {
  FiX,
  FiMail,
  FiCopy,
  FiCheck,
  FiAlertTriangle,
  FiCheckCircle,
  FiEye,
  FiClock,
  FiBriefcase,
  FiTag,
} from "react-icons/fi";
import { formatDateTime } from "../utils/formatDate";
import toast from "react-hot-toast";

export default function EmailHistorySnapshotModal({ log, onClose }) {
  const [copiedSubject, setCopiedSubject] = useState(false);
  const [copiedBody, setCopiedBody] = useState(false);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  if (!log) return null;

  const handleCopySubject = () => {
    if (!log.subject) return;
    navigator.clipboard.writeText(log.subject);
    setCopiedSubject(true);
    toast.success("Subject copied!");
    setTimeout(() => setCopiedSubject(false), 2000);
  };

  const handleCopyBody = () => {
    if (!log.body) return;
    navigator.clipboard.writeText(log.body);
    setCopiedBody(true);
    toast.success("Email text copied!");
    setTimeout(() => setCopiedBody(false), 2000);
  };

  // Status configuration
  const statusConfig = {
    Sent: {
      badge: "bg-emerald-50 text-emerald-700 border-emerald-200",
      dot: "bg-emerald-500",
      icon: <FiCheckCircle size={13} className="text-emerald-600" />,
      label: "Delivered / Sent",
    },
    Opened: {
      badge: "bg-blue-50 text-blue-700 border-blue-200",
      dot: "bg-blue-500",
      icon: <FiEye size={13} className="text-blue-600" />,
      label: "Opened by Contact",
    },
    Failed: {
      badge: "bg-red-50 text-red-700 border-red-200",
      dot: "bg-red-500",
      icon: <FiAlertTriangle size={13} className="text-red-600" />,
      label: "Failed to Deliver",
    },
    Pending: {
      badge: "bg-amber-50 text-amber-700 border-amber-200",
      dot: "bg-amber-500",
      icon: <FiClock size={13} className="text-amber-600" />,
      label: "Pending in Queue",
    },
  };

  const currentStatus = statusConfig[log.status] || {
    badge: "bg-gray-100 text-gray-700 border-gray-200",
    dot: "bg-gray-400",
    icon: <FiClock size={13} />,
    label: log.status || "Sent",
  };

  // Extract initials for the avatar
  const getInitials = (name, email) => {
    if (name && name.trim()) {
      const parts = name.trim().split(" ");
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return name.slice(0, 2).toUpperCase();
    }
    if (email) {
      return email.slice(0, 2).toUpperCase();
    }
    return "EM";
  };

  const initials = getInitials(log.contact_name, log.contact_email);

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-50 p-4 sm:p-6 animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col border border-gray-200/80"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top Header Bar */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
          <div className="flex items-center gap-2.5">
            <span className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
              <FiMail size={16} />
            </span>
            <div>
              <h2 className="text-sm font-semibold text-gray-900">Sent Email Details</h2>
              <span className="text-[11px] text-gray-400">Log #{log.id}</span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${currentStatus.badge}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${currentStatus.dot}`} />
              {currentStatus.label}
            </span>
            <button
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition"
              title="Close (Esc)"
            >
              <FiX size={18} />
            </button>
          </div>
        </div>

        {/* Modal Scrollable Content: Email Message View */}
        <div className="flex-1 overflow-y-auto p-6 sm:p-8 space-y-6">
          {/* Failure Alert Banner (if failed) */}
          {log.status === "Failed" && log.failed_reason && (
            <div className="p-4 bg-red-50/90 border border-red-200 rounded-xl text-xs text-red-900 flex items-start gap-3">
              <FiAlertTriangle className="shrink-0 text-red-600 mt-0.5" size={16} />
              <div className="flex-1">
                <span className="font-semibold block mb-0.5">Delivery Error:</span>
                <p className="font-mono text-[11px] text-red-700 whitespace-pre-wrap break-all">
                  {log.failed_reason}
                </p>
              </div>
            </div>
          )}

          {/* Email Header Section */}
          <div className="space-y-4 pb-6 border-b border-gray-100">
            {/* Subject Line */}
            <div className="flex items-start justify-between gap-4">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 leading-tight">
                {log.subject || <span className="text-gray-400 font-normal italic">No Subject</span>}
              </h1>
              <button
                onClick={handleCopySubject}
                className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-gray-600 hover:text-gray-900 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg transition"
                title="Copy subject"
              >
                {copiedSubject ? (
                  <>
                    <FiCheck size={12} className="text-emerald-600" />
                    <span className="text-emerald-600">Copied</span>
                  </>
                ) : (
                  <>
                    <FiCopy size={12} />
                    <span>Copy</span>
                  </>
                )}
              </button>
            </div>

            {/* Recipient & Metadata Details Row */}
            <div className="flex items-center justify-between gap-4 pt-1">
              <div className="flex items-center gap-3">
                {/* Avatar */}
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-600 to-indigo-700 text-white font-bold text-xs flex items-center justify-center shadow-xs shrink-0">
                  {initials}
                </div>

                {/* Recipient Info */}
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-900 text-sm">
                      {log.contact_name || "Contact"}
                    </span>
                    <span className="text-xs text-gray-500 font-mono">
                      &lt;{log.contact_email}&gt;
                    </span>
                  </div>

                  <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5 flex-wrap">
                    {log.contact_company && (
                      <span className="flex items-center gap-1">
                        <FiBriefcase size={12} className="text-gray-400" />
                        {log.contact_company}
                      </span>
                    )}
                    {log.contact_category && (
                      <span className="flex items-center gap-1 text-blue-600">
                        <FiTag size={11} />
                        {log.contact_category}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Timestamp */}
              <div className="text-right shrink-0">
                <span className="text-xs font-medium text-gray-700 block">
                  {log.sent_at ? formatDateTime(log.sent_at) : "Pending"}
                </span>
                {log.opened_at && (
                  <span className="text-[11px] text-blue-600 block mt-0.5">
                    Opened {formatDateTime(log.opened_at)}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Email Body Content */}
          <div className="py-2">
            <div className="text-gray-800 text-[15px] leading-relaxed whitespace-pre-wrap font-normal select-text space-y-4">
              {log.body ? (
                log.body
              ) : (
                <p className="text-gray-400 italic">No email body recorded.</p>
              )}
            </div>

            {/* Unsubscribe Footer */}
            <div className="mt-10 pt-4 border-t border-gray-100 text-xs text-gray-400 leading-relaxed">
              If you no longer wish to receive these emails,{" "}
              <span className="text-gray-400 underline cursor-not-allowed">
                unsubscribe here
              </span>
              .
            </div>
          </div>
        </div>

        {/* Modal Bottom Action Bar */}
        <div className="px-6 py-3.5 bg-gray-50 border-t border-gray-100 flex items-center justify-between gap-3">
          <button
            onClick={handleCopyBody}
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-white hover:bg-gray-100 text-gray-700 border border-gray-200 rounded-lg text-xs font-semibold transition shadow-2xs"
          >
            {copiedBody ? (
              <>
                <FiCheck size={13} className="text-emerald-600" />
                <span className="text-emerald-600">Copied Email Text!</span>
              </>
            ) : (
              <>
                <FiCopy size={13} />
                <span>Copy Email Text</span>
              </>
            )}
          </button>

          <button
            onClick={onClose}
            className="px-4 py-1.5 bg-gray-900 hover:bg-black text-white text-xs font-semibold rounded-lg transition shadow-xs"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
